import * as React from 'react';
import 'twin.macro';
import { StaticImage } from 'gatsby-plugin-image';

import Layout from '~components/Layout';
import SEO from '~components/SEO';
import Heading from '~components/Heading';
import Text from '~components/Text';
import Button from '~components/Button';
import FullscreenVideo from '~components/FullscreenVideo';

import PenPaper from '~components/Icon/PenPaper';
import CheckboxPaper from '~components/Icon/CheckboxPaper';
import Manuscript from '~components/Icon/Manuscript';
import OnPhoneComputer from '~components/Icon/OnPhoneComputer';
import HoldingCert from '~components/Icon/HoldingCert';
import HoldingMoney from '~components/Icon/HoldingMoney';

import homeCareScienceWebm from '../videos/home-care-science.webm';
import homeCareScienceMp4 from '../videos/home-care-science.mp4';

import fileName from '/src/pdf/2021-RI-22-03-30.pdf'

const HomeCarePage: React.FC = () => (
  <Layout>
    <SEO title="Investing in Home Care Science" desc="" />
    <FullscreenVideo
      src={{
        webm: homeCareScienceWebm,
        mp4: homeCareScienceMp4
      }}
    >
      <div tw="bg-[#480239] text-white bg-opacity-95 pt-12">
        <div tw="grid lg:(grid-cols-2 gap-12)">
          <div tw="flex flex-col items-center justify-center lg:(col-start-2 row-start-1)">
            <div tw="px-6 lg:px-12">
              <Heading tw="leading-tight my-0" level="h1">
                Investing in Home Care Science
              </Heading>
            </div>
          </div>
          <div tw="lg:(col-start-1 row-start-1)">
            <StaticImage src="../images/donabelle-hero.png" alt="" />
          </div>
        </div>
      </div>
    </FullscreenVideo>
    <div tw="bg-[#480239] text-white py-6 md:py-12">
      <div tw="container">
        <Heading level="h2">PSW Donnabelle Lasban Drives Positive Change as One of VHA's Research Fellows</Heading>
        <p tw="text-base md:text-xl my-4">
        VHA is committed to making care better for clients and providers by investing in home care science. VHA’s researchers work closely with academic partners on projects to advance a more connected health and community care system and to support safer teams, including recent studies on the impact of COVID-19 on frontline home care workers. VHA’s investments also include annual programs to support graduate student researchers and to provide VHA therapists, nurses and personal support workers (PSWs) with fellowship opportunities to develop their quality improvement and evaluation skills to position them as practice leaders.
        </p>
        <p tw="text-base md:text-xl my-4">
        Donnabelle Lasban is one of VHA’s current fellows. A PSW at VHA since 2017 with a background in nursing before moving to Canada, Donnabelle takes pride in continuously learning how to improve the care she provides. As a part of VHA’s Champions of Change Interprofessional Fellowship program, Donnabelle is working with other point-of-care nurses and PSWs to design and lead a quality improvement project focused on the use of technology to support clients and families living with dementia to manage their medications. By developing, implementing and evaluating this initiative, Donnabelle is helping to drive positive change within her clinical team.
        </p>
        <p tw="text-base md:text-xl my-4">
        “I believe that if you’re open to new ideas, are willing to embrace change and think critically, you can provide even higher quality care,” Donnabelle shared. “I'm excited about the fellowship opportunity and to be involved in this quality improvement project. I look forward to sharing it with my team.”
        </p>
      </div>
      <div tw="container">
        <div tw="grid gap-12 my-12 lg:(grid-cols-2)">
          <div tw="lg:(row-start-1 col-start-2)">
            <StaticImage src="../images/research-report.png" alt="" />
          </div>
          <div tw="lg:(row-start-1 col-start-1)">
            <Heading level="h2">Research & Innovation Annual Report 2021</Heading>
            <p tw="text-base md:text-xl my-4">
            In 2021, VHA elevated its commitment to home care science with the creation of the inaugural Vice President, Research & Innovation position at VHA. This role is focused on bringing to life VHA’s transformational vision to advance and inspire improvements in home care through research and innovation. Learn more about our vision and recent work in VHA's 2021 Research & Innovation Annual Report.
            </p>
            <Text>
              <Button as="a" href={fileName}>
              View Research & Innovation Annual Report 2021
                </Button>
            </Text>
          </div>
        </div>
        <Heading level="h2">Research By the Numbers</Heading>
        <div tw="grid gap-6 md:(grid-cols-2) lg:(grid-cols-3 gap-12)">
          <div tw="flex items-center space-x-4 md:space-x-6">
            <PenPaper tw="w-20 h-20 lg:(w-28 h-28)" />
            <p>
              <span tw="block text-2xl md:text-4xl font-bold">30</span>
              <span tw="block text-lg">active research studies</span>
            </p>
          </div>
          <div tw="flex items-center space-x-4 md:space-x-6">
            <HoldingCert tw="w-20 h-20 lg:(w-28 h-28)" />
            <p>
              <span tw="block text-2xl md:text-4xl font-bold">23</span>
              <span tw="block text-lg">academic trainees</span>
            </p>
          </div>
          <div tw="flex items-center space-x-4 md:space-x-6">
            <OnPhoneComputer tw="w-20 h-20 lg:(w-28 h-28)" />
            <p>
              <span tw="block text-2xl md:text-4xl font-bold">52</span>
              <span tw="block text-lg">client &amp; provider partners engaged</span>
            </p>
          </div>
          <div tw="flex items-center space-x-4 md:space-x-6">
            <Manuscript tw="w-20 h-20 lg:(w-28 h-28)" />
            <p>
              <span tw="block text-2xl md:text-4xl font-bold">5</span>
              <span tw="block text-lg">manuscripts published</span>
            </p>
          </div>
          <div tw="flex items-center space-x-4 md:space-x-6">
            <CheckboxPaper tw="w-20 h-20 lg:(w-28 h-28)" />
            <p>
              <span tw="block text-2xl md:text-4xl font-bold">14</span>
              <span tw="block text-lg">grant applications approved</span>
            </p>
          </div>
          <div tw="flex items-center space-x-4 md:space-x-6">
            <HoldingMoney tw="w-20 h-20 lg:(w-36 h-36)" />
            <p>
              <span tw="block text-2xl md:text-4xl font-bold">$1,000,000</span>
              <span tw="block text-lg">in grant funding received with partners</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </Layout>
);

export default HomeCarePage;
